import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { listMyOrders, type OrderStatus } from '../../lib/catalog';
import { ApiError } from '../../lib/apiClient';
import { formatDate, formatDateTime, formatMoney, formatTime, shortId } from '../../lib/format';
import { TicketQr } from '../../components/TicketQr';
import './BuyerPages.css';

/**
 * Order history for a signed-in buyer.
 *
 * Only orders where orders.buyer_id matches the caller — the engine enforces
 * that, and a guest checkout made with the same email address is deliberately
 * NOT included, because the engine never linked it to an account. Those tickets
 * live in the buyer's email and behind the order token from checkout.
 */
export function OrdersPage() {
  const [orders, setOrders] = useState<OrderStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    listMyOrders()
      .then((result) => {
        if (!cancelled) setOrders(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load your orders.');
        setOrders([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <p className="buyer__status">Loading your orders…</p>;

  if (error) {
    return (
      <p className="buyer__status buyer__status--error" role="alert">
        {error}
      </p>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="buyer__empty">
        <h2>No orders yet</h2>
        <p>Tickets you buy while signed in will appear here.</p>
        <Link to="/events" className="button button--primary">
          Browse events
        </Link>
      </div>
    );
  }

  return (
    <div className="buyer__page">
      <header className="buyer__header">
        <h1 className="buyer__title display">My orders</h1>
        <p className="buyer__lede">
          <span className="mono">{orders.length}</span>{' '}
          {orders.length === 1 ? 'order' : 'orders'}
        </p>
      </header>

      <ul className="order-list">
        {orders.map((order) => {
          const time = formatTime(order.event.time);
          const active = order.tickets.filter((t) => t.qr != null);
          return (
            <li key={order.orderId} className="order card">
              <div className="order__head">
                <div>
                  <h2 className="order__event">
                    <Link to={`/events/${order.event.eventId}`}>{order.event.title}</Link>
                  </h2>
                  <p className="order__meta">
                    {formatDate(order.event.date)}
                    {time ? ` · ${time}` : ''}
                    {order.event.venue ? ` · ${order.event.venue}` : ''}
                  </p>
                </div>
                <span className={`order__status order__status--${order.status}`}>
                  {order.status}
                </span>
              </div>

              <dl className="order__facts">
                <div>
                  <dt>Order</dt>
                  <dd className="mono" title={order.orderId}>
                    {shortId(order.orderId)}
                  </dd>
                </div>
                <div>
                  <dt>Placed</dt>
                  <dd className="mono">{formatDateTime(order.createdAt)}</dd>
                </div>
                <div>
                  <dt>Total</dt>
                  <dd className="mono">{formatMoney(order.totalCents)}</dd>
                </div>
                <div>
                  <dt>Tickets</dt>
                  <dd className="mono">{order.tickets.length}</dd>
                </div>
              </dl>

              {active.length > 0 ? (
                <div className="order__tickets">
                  {order.tickets.map((ticket, index) => (
                    <TicketQr key={ticket.ticketId} ticket={ticket} index={index + 1} />
                  ))}
                </div>
              ) : (
                <p className="order__note">
                  {order.status === 'pending'
                    ? 'Awaiting payment confirmation, QR codes appear once payment settles.'
                    : 'No active tickets on this order.'}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
